import { getKnownIslandDimensionDefaults, getSurroundFinishProfile, SurroundFinishProfile } from '../../../helpers/finish-profiles';

import { Island } from '../island.class';
import { IslandType } from '../enums';
import { setSizeCornerPanelsCfn } from './set-size-corner-panels.cfn';
import { setSizeStandardPanelsCfn } from './set-size-standard-panels.cfn';

import type { Size } from '../../../types';

// Snaps height/depth to the target finish's defaults when the user hasn't customized.
// Bidirectional — fires whenever current size matches ANY known preset across all
// finish profiles (i.e., the user hasn't manually typed in a custom value).
const trySnapSize = (island: Island, profile: SurroundFinishProfile): boolean => {
	if (island.panelThicknessConfig) return false;
	const target = profile.islandDimensions[island.type];
	if (!target) return false;

	const size = island.size as Size;
	const knownDefaults = getKnownIslandDimensionDefaults(island.type);
	const matchesKnown = knownDefaults.some((d) => d.height === size.height && d.depth === size.depth);
	if (!matchesKnown) return false;
	if (size.height === target.height && size.depth === target.depth) return false;

	island.size = { height: target.height, depth: target.depth };
	return true;
};

// Per-panel overhang remap. Only fires on a family transition; uses the incoming profile's
// remap table for the SPECIFIC family being left (see finish-profiles.ts) -- not value alone --
// so a value is only ever reinterpreted once we know exactly which family it came from. Values
// with no entry for the outgoing family, or not present in that family's map, are left alone.
const remapPanelOverhangs = (island: Island, prevProfile: SurroundFinishProfile, nextProfile: SurroundFinishProfile): void => {
	const remap = nextProfile.overhangRemapEnteringFrom[prevProfile.key];
	if (!remap) return;
	island.panels.children.forEach((panel) => {
		const next = remap.get(panel.countertopOverhang);
		if (next !== undefined && next !== panel.countertopOverhang) panel.countertopOverhang = next;
	});
};

/**
 * Reacts to a `surround_material.updated` event for a single island. On any cross-family
 * transition (standard / MOK / slat): remaps per-panel overhangs, then snaps Grill/Bar
 * height/depth to the target finish's defaults if uncustomized. On any change (family
 * transition or same-family): re-sizes panels for the new material thickness. Called
 * per-island by the rules engine, using THIS island's own `surround` (its override, or the
 * shared kitchen instance when it has none) -- so an island overridden away from whichever
 * SurroundMaterial instance actually changed sees no transition and is correctly skipped.
 */
export const applySurroundMaterialUpdateCfn = (island: Island): void => {
	const surround = island.surround;
	const prevProfile = getSurroundFinishProfile(surround?.previousMaterial);
	const nextProfile = getSurroundFinishProfile(surround?.material);
	const isFamilyTransition = !!prevProfile && !!nextProfile && prevProfile !== nextProfile;

	if (isFamilyTransition && prevProfile && nextProfile) remapPanelOverhangs(island, prevProfile, nextProfile);

	if (nextProfile && trySnapSize(island, nextProfile)) return;

	// Load-time guard: sibling islands publish surround_material.updated before their
	// panels finish hydrating. Corner skips this since side widths depend on front AND back.
	if (!island.panelThicknessConfig && island.type !== IslandType.Corner) {
		const currentFrontDepth = island.frontPanel?.size?.depth;
		const newFrontThickness = surround?.getThicknessForRole('front');
		if (currentFrontDepth !== undefined && currentFrontDepth === newFrontThickness) return;
	}

	if (island.type === IslandType.Corner) setSizeCornerPanelsCfn(island);
	else setSizeStandardPanelsCfn(island);
};
